import { useState } from "react";
import Collapse from "../../components/UI/Collapse";
import { DollarSign, QrCode, CreditCard, Percent } from "lucide-react";
import MetricsToggle from "../../components/MetricsToggle/MetricsToggle";
import FiltersSection from "../../components/Filter/Filter";
import { salesService } from "../../services/api";
import { useAuth } from "../../context/AuthProvider.jsx";
import { fmtCurrencyBR, fmtNumberBR, fmtCurrency } from "../../utils/formatBrl";
import { truncate } from "../../utils/truncate";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import { StatCardsSkeleton, TableSkeleton } from "../../components/SkeletonLoading/PageSkeletons";
import { useBotPhotosByUsername } from "../../hooks/useBotPhotos";
import BotAvatar from "../../components/BotAvatar/BotAvatar";
import BotChip from "../../components/BotChip/BotChip";
import StatusPill from "../../components/StatusPill/StatusPill";
import { getVisibleBots } from "../../utils/bots";

export default function Sales() {
    const { user } = useAuth();
    const photoByUsername = useBotPhotosByUsername(user?.bots || []);
    const visibleBots = getVisibleBots(user?.bots);

    const [metrics, setMetrics] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        gatewayId: null,
        botIds: [],
        paymentStatus: null,
        paymentMethod: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showMetrics, setShowMetrics] = useState(false);

    const fetchSales = async (page = 1, currentFilters = filters) => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page,
                per_page: 20,
            };

            if (currentFilters.startDate) params.start_date = currentFilters.startDate;
            if (currentFilters.endDate) params.end_date = currentFilters.endDate;
            if (currentFilters.gatewayId) params.gateway_id = currentFilters.gatewayId;
            if (currentFilters.botIds && currentFilters.botIds.length > 0) params.bot_ids = currentFilters.botIds.join(',');
            if (currentFilters.paymentStatus) params.payment_status = currentFilters.paymentStatus;
            if (currentFilters.paymentMethod) params.payment_method = currentFilters.paymentMethod;

            const response = await salesService.getSales(params);
            const data = response.data?.data;

            if (!response.data?.success || !data) {
                throw new Error(response.data?.message || "Falha ao carregar dados financeiros");
            }

            setMetrics(data.metrics || null);
            setTransactions(data.transactions || []);
            setPagination(data.pagination || null);
        } catch (err) {
            console.error("Erro ao carregar financeiro:", err);
            setError(err.message || "Erro ao carregar financeiro");
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = (filterData) => {
        const nextFilters = {
            startDate: filterData.startDate,
            endDate: filterData.endDate,
            gatewayId: filterData.gatewayId || null,
            botIds: filterData.botIds || [],
            paymentStatus: filterData.paymentStatus || null,
            paymentMethod: filterData.paymentMethod || null,
        };
        setFilters(nextFilters);
        // página sempre volta para 1 ao aplicar novo filtro
        fetchSales(1, nextFilters);
    };

    const handleNextPage = () => {
        if (!pagination?.has_next) return;
        const nextPage = pagination.page + 1;
        fetchSales(nextPage, filters);
        setPagination((prev) => (prev ? { ...prev, page: nextPage } : prev));
    };

    const handlePrevPage = () => {
        if (!pagination?.has_prev) return;
        const prevPage = pagination.page - 1;
        fetchSales(prevPage, filters);
        setPagination((prev) => (prev ? { ...prev, page: prevPage } : prev));
    };

    const formatStatusLabel = (status) => {
        if (!status) return "-";
        const normalized = status.toLowerCase();
        if (normalized === "approved") return "Pago";
        if (normalized === "pending") return "Pendente";
        if (normalized === "expired") return "Expirado";
        if (normalized === "failed") return "Falhou";
        return status;
    };

    const getStatusClass = (status) => {
        const normalized = (status || "").toLowerCase();
        if (normalized === "approved") return "text-green-400";
        if (normalized === "pending") return "text-yellow-400";
        if (normalized === "expired") return "text-gray-400";
        if (normalized === "failed") return "text-red-400";
        return "text-gray-300";
    };

    const getBgClass = (status) => {
        const normalized = (status || "").toLowerCase();
        if (normalized === "approved") return "bg-green-900";
        if (normalized === "pending") return "bg-yellow-900";
        if (normalized === "expired") return "bg-gray-900";
        if (normalized === "failed") return "bg-red-900";
        return "bg-gray-300";
    }

    // Cor do StatusPill (card mobile)
    const getStatusColor = (status) => {
        const normalized = (status || "").toLowerCase();
        if (normalized === "approved") return "green";
        if (normalized === "pending") return "yellow";
        if (normalized === "expired") return "gray";
        if (normalized === "failed") return "red";
        return "gray";
    };

    // Barrinha de progresso da taxa de conversão (0–100%).
    const ConvBar = ({ value, color = "var(--success)" }) => {
        const pct = Math.max(0, Math.min(100, Number(value) || 0));
        return (
            <div
                style={{
                    width: "100%",
                    height: 4,
                    marginTop: 8,
                    background: "rgba(148, 163, 184, 0.1)",
                    borderRadius: 2,
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: color,
                        borderRadius: 2,
                        transition: "width 0.6s ease-in-out",
                    }}
                />
            </div>
        );
    };

    // Badge do método de pagamento (PIX x Cartão).
    const MethodBadge = ({ method }) => {
        const isCard = (method || "pix").toLowerCase() === "card";
        return (
            <span
                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded"
                style={{
                    color: isCard ? "#c4b5fd" : "#86efac",
                    background: isCard ? "rgba(139,92,246,0.15)" : "rgba(34,197,94,0.15)",
                    // border: `0.1px solid ${isCard ? "#8b5cf6" : "#22c55e"}`,
                    whiteSpace: "nowrap",
                }}
            >
                {isCard ? <CreditCard size={12} strokeWidth={2.5} /> : <QrCode size={12} strokeWidth={2.5} />}
                {isCard ? "Cartão" : "PIX"}
            </span>
        );
    };

    // Badge de venda global (cartão em moeda estrangeira). O valor exibido já vem
    // convertido em BRL do backend; a badge sinaliza a moeda original.
    const CurrencyBadge = ({ tx }) => {
        if (!tx?.currency || tx.currency === "BRL") return null;
        return (
            <span
                className="sx-currency-badge"
                title={`Venda global (cartão) — valor original ${fmtCurrency(tx.currency, tx.amount_original)}`}
                style={{
                    marginLeft: 6,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#60a5fa",
                    border: "0.5px solid #3b82f6",
                    borderRadius: 4,
                    padding: "1px 4px",
                    whiteSpace: "nowrap",
                }}
            >
                🌎 {tx.currency}
            </span>
        );
    };

    return (
        <main className="page">
            <div className="page-header-with-filters">
                <div className="page-header-content flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h1 className="title">Financeiro</h1>
                        <p>Gerencie suas receitas e transações</p>
                    </div>
                    <MetricsToggle open={showMetrics} onToggle={() => setShowMetrics((v) => !v)} />
                </div>
            </div>

            <FiltersSection
                onApplyFilters={handleApplyFilters}
                bots={visibleBots}
                gateway={user?.gateways || []}
                showGatewayFilter={true}
                showStatusFilter={false}
                showFluxoFilter={false}
                showStartsFilter={false}
                showPaymentStatusFilter={true}
                showPaymentMethodFilter={true}
                enableBotMultiSelect={true}
                showBotFilter={true}
            />

            {error && (
                <p className="error-message" style={{ marginTop: 16 }}>
                    {error}
                </p>
            )}

            {/* Métricas principais */}
            {/* Os 24px acima vêm da margem dos filtros. Fechado, o bloco devolve
                esses 24px: sem isso as margens de filtros e tabela somariam. */}
            <Collapse open={showMetrics} closedClassName="-mt-6">
                {/* Margem de baixo zerada: dentro do Collapse ela somaria com a da tabela. */}
                <div className="stats-grid spt-reveal-stagger" style={{ marginBottom: 0 }}>
                    {loading ? (
                        <StatCardsSkeleton count={4} />
                    ) : metrics && (() => {
                        const total = metrics.total || {};
                        const pix = metrics.pix || {};
                        const card = metrics.card || {};
                        const pct = (v) => `${(Number(v) || 0).toFixed(1)}%`;
                        return (
                        <>
                            {/* Receita Total (PIX + Cartão) */}
                            <div className="stat-card">
                                <div className="stat-card-header">
                                    <div className="stat-icon green">
                                        <DollarSign size={20} strokeWidth={2} />
                                    </div>
                                    <div className="stat-title">Receita Total</div>
                                </div>
                                <div className="stat-value">
                                    {fmtCurrencyBR.format(total.paid_amount || 0)}
                                </div>
                                <div className="stat-change">
                                    {fmtNumberBR.format(total.paid_count || 0)} pagamentos aprovados
                                </div>
                            </div>

                            {/* PIX pagos + conversão PIX */}
                            <div className="stat-card">
                                <div className="stat-card-header">
                                    <div className="stat-icon purple">
                                        <QrCode size={20} strokeWidth={2} />
                                    </div>
                                    <div className="stat-title">PIX Pagos</div>
                                </div>
                                <div className="stat-value">
                                    {fmtCurrencyBR.format(pix.paid_amount || 0)}
                                </div>
                                <div className="stat-change">
                                    {`${pct(pix.conversion_rate)} · ${pix.paid_count || 0}/${pix.generated_count || 0} gerados`}
                                </div>
                                <ConvBar value={pix.conversion_rate} color="#8b5cf6" />
                            </div>

                            {/* Cartão pagos + conversão Cartão */}
                            <div className="stat-card">
                                <div className="stat-card-header">
                                    <div className="stat-icon blue">
                                        <CreditCard size={20} strokeWidth={2} />
                                    </div>
                                    <div className="stat-title">Cartão Pagos</div>
                                </div>
                                <div className="stat-value">
                                    {fmtCurrencyBR.format(card.paid_amount || 0)}
                                </div>
                                <div className="stat-change">
                                    {`${pct(card.conversion_rate)} · ${card.paid_count || 0}/${card.generated_count || 0} pagamentos`}
                                </div>
                                <ConvBar value={card.conversion_rate} color="#3b82f6" />
                            </div>

                            {/* Conversão geral (todos os métodos) */}
                            <div className="stat-card">
                                <div className="stat-card-header">
                                    <div className="stat-icon green">
                                        <Percent size={20} strokeWidth={2} />
                                    </div>
                                    <div className="stat-title">Taxa de Conversão</div>
                                </div>
                                <div className="stat-value">
                                    {pct(total.conversion_rate)}
                                </div>
                                <div className="stat-change">
                                    {`${total.paid_count || 0} pagos de ${total.generated_count || 0} gerados`}
                                </div>
                                <ConvBar value={total.conversion_rate} />
                            </div>
                        </>
                        );
                    })()}
                </div>
            </Collapse>

            {/* Transações recentes */}
            <section style={{ marginTop: 32 }}>
                {loading ? (
                    <TableSkeleton label="Carregando transações" columns={["Lead", "Bot", "Tipo do Plano", "Nome do Plano", "Valor", "Data", "Método", "Status"]} />
                ) : transactions.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-receipt" />
                        <h4>Nenhuma transação encontrada</h4>
                        <p>As transações do período selecionado aparecerão aqui.</p>
                    </div>
                ) : (
                    <>
                        <div className="sx-desktop spt-reveal">
                            <table className="sx-table">
                                <thead>
                                    <tr>
                                        <th>Lead</th>
                                        <th>Bot</th>
                                        <th>Tipo do Plano</th>
                                        <th>Nome do Plano</th>
                                        <th>Valor</th>
                                        <th>Data</th>
                                        <th>Método</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((tx) => (
                                        <tr key={tx.id}>
                                            <td className="truncate max-w-xs" title={tx.lead_name || tx.pix_code}>
                                                {truncate(tx.lead_name || tx.pix_code, 14)}
                                            </td>
                                            <td className="max-w-[140px] overflow-hidden text-xs text-gray-400" title={tx.bot_username || ""}>
                                                <span className="flex items-center gap-1.5 min-w-0">
                                                    <BotAvatar photoUrl={photoByUsername[tx.bot_username]} size={32} />
                                                    <span className="truncate min-w-0 flex-1">{tx.bot_username || "-"}</span>
                                                </span>
                                            </td>
                                            <td className="max-w-[160px]">

                                                {tx.plan_type && <div className="font-semibold truncate">{tx.plan_type}</div>}
                                            </td>
                                            <td className="max-w-[120px]">

                                                {tx.plan_name && <div className="text-xs text-gray-400 truncate" title={tx.plan_name}>
                                                    {tx.plan_name.replace("Plano ", "")}
                                                </div>}
                                            </td>
                                            <td className="font-bold">
                                                {fmtCurrencyBR.format(tx.amount || 0)}
                                                <CurrencyBadge tx={tx} />
                                            </td>
                                            <td>
                                                <div className="font-bold text-base text-white">
                                                    {fmtDateTimezone(tx.created_at, 'time')}h
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                    {fmtDateTimezone(tx.created_at, 'date')}
                                                </div>
                                            </td>
                                                                                        <td>
                                                <MethodBadge method={tx.payment_method} />
                                            </td>
                                            <td className={getStatusClass(tx.status)}>
                                                <div className={getBgClass(tx.status) + " font-bold mx-auto inline-block px-2 py-1 rounded text-xs font-semibold"}>
                                                    {formatStatusLabel(tx.status)}
                                                </div>

                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="sx-mobile spt-reveal">
                            {transactions.map((tx) => {
                                const displayName = tx.lead_name || "Cliente padrão";
                                return (
                                    <div key={tx.id} className="sx-card">
                                        {/* Header: lead + status */}
                                        <div className="flex items-center justify-between gap-3 mb-3">
                                            <div className="font-semibold text-white truncate min-w-0" title={displayName}>
                                                {truncate(displayName, 14)}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <MethodBadge method={tx.payment_method} />
                                                <StatusPill color={getStatusColor(tx.status)}>
                                                    {formatStatusLabel(tx.status)}
                                                </StatusPill>
                                            </div>
                                        </div>

                                        {/* Bot */}
                                        {tx.bot_username && (
                                            <div className="flex flex-wrap gap-1.5 mb-3">
                                                <BotChip
                                                    photoUrl={photoByUsername[tx.bot_username]}
                                                    label={tx.bot_username}
                                                />
                                            </div>
                                        )}

                                        {/* Valor + Plano + Data/Hora */}
                                        <div className="sx-stats mb-3">
                                            <div className="sx-stat">
                                                <div className="sx-stat-value sx-stat-value--green" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 4, rowGap: 2 }}>
                                                    <span>{fmtCurrencyBR.format(tx.amount || 0)}</span>
                                                    <CurrencyBadge tx={tx} />
                                                </div>
                                                <div className="sx-stat-label">Valor</div>
                                            </div>
                                            <div className="sx-stat">
                                                <div className="sx-stat-value" title={tx.plan_name || ""}>
                                                    {tx.plan_name ? tx.plan_name.replace("Plano ", "") : "—"}
                                                </div>
                                                <div className="sx-stat-label">{tx.plan_type || "Plano"}</div>
                                            </div>
                                            <div className="sx-stat">
                                                <div className="sx-stat-value">
                                                    {fmtDateTimezone(tx.created_at, 'time')}h
                                                </div>
                                                <div className="sx-stat-label">{fmtDateTimezone(tx.created_at, 'date')}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {pagination && (
                            <div className="mt-8 flex items-center justify-between">
                                <div className="text-sm text-gray-600">
                                    Página {pagination.page} de {pagination.total_pages || 1} ({pagination.total} transações)
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={handlePrevPage}
                                        disabled={!pagination.has_prev}
                                        className="btn-secondary"
                                        style={{ opacity: pagination.has_prev ? 1 : 0.5 }}
                                    >
                                        <i className="fas fa-chevron-left"></i> Anterior
                                    </button>

                                    <button
                                        onClick={handleNextPage}
                                        disabled={!pagination.has_next}
                                        className="btn-primary"
                                        style={{ opacity: pagination.has_next ? 1 : 0.5 }}
                                    >
                                        Próximo <i className="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </section>

            {/* Métodos de pagamento removidos conforme solicitado */}
        </main >
    );
}
